import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, Users } from 'lucide-react';
import { CreateRelationshipData, MentorProfile, MenteeProfile } from '../../../types/admin';
import apiService from '../../../services/api';

interface CreateRelationshipModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateRelationshipModal: React.FC<CreateRelationshipModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingMentors, setLoadingMentors] = useState(true);
  const [loadingMentees, setLoadingMentees] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [mentors, setMentors] = useState<MentorProfile[]>([]);
  const [mentees, setMentees] = useState<MenteeProfile[]>([]);

  const [formData, setFormData] = useState<CreateRelationshipData>({
    mentor_id: 0,
    mentee_id: 0,
    start_date: new Date().toISOString().split('T')[0],
    relationship_goals: '',
  });

  const [selectedMentor, setSelectedMentor] = useState<MentorProfile | null>(null);
  const [selectedMentee, setSelectedMentee] = useState<MenteeProfile | null>(null);

  useEffect(() => {
    loadMentors();
    loadMentees();
  }, []);

  const loadMentors = async () => {
    try {
      setLoadingMentors(true);
      const response = await apiService.admin.getMentors();
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);

      // Transform and add availability
      const mentorsWithAvailability = data.map((m: any) => ({
        ...m,
        availability: m.current_mentees_count >= m.max_mentees ? 'full' :
                     m.current_mentees_count >= m.max_mentees * 0.8 ? 'limited' : 'available'
      }));

      setMentors(mentorsWithAvailability);
    } catch (err) {
      console.error('Error loading mentors:', err);
      setMentors([]);
    } finally {
      setLoadingMentors(false);
    }
  };

  const loadMentees = async () => {
    try {
      setLoadingMentees(true);
      const response = await apiService.admin.getMentees();
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);

      // Only show mentees without active mentor
      const availableMentees = data.filter((m: any) => !m.has_active_mentor);
      setMentees(availableMentees);
    } catch (err) {
      console.error('Error loading mentees:', err);
      setMentees([]);
    } finally {
      setLoadingMentees(false);
    }
  };

  const handleMentorChange = (mentorId: string) => {
    const id = parseInt(mentorId);
    setFormData(prev => ({ ...prev, mentor_id: id }));
    const mentor = mentors.find(m => m.id === id);
    setSelectedMentor(mentor || null);
  };

  const handleMenteeChange = (menteeId: string) => {
    const id = parseInt(menteeId);
    setFormData(prev => ({ ...prev, mentee_id: id }));
    const mentee = mentees.find(m => m.id === id);
    setSelectedMentee(mentee || null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.mentor_id || !formData.mentee_id) {
      setError('Debes seleccionar un mentor y un mentee');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await apiService.admin.createRelationship(formData);
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      console.error('Error creating relationship:', err);
      setError(err.response?.data?.message || 'Error al crear la relación');
    } finally {
      setLoading(false);
    }
  };

  const getAvailabilityBadge = (availability: string) => {
    const badges = {
      available: { class: 'badge-success', label: 'Disponible' },
      limited: { class: 'badge-warning', label: 'Capacidad limitada' },
      full: { class: 'badge-danger', label: 'Capacidad completa' },
    };
    const badge = badges[availability as keyof typeof badges] || badges.available;
    return <span className={`availability-badge ${badge.class}`}>{badge.label}</span>;
  };

  if (success) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal modal--medium" onClick={(e) => e.stopPropagation()}>
          <div className="modal__body">
            <div className="success-message">
              <CheckCircle size={64} className="success-icon" />
              <h3>¡Relación Creada Exitosamente!</h3>
              <p>
                La dupla entre {selectedMentor?.first_name} {selectedMentor?.last_name} y{' '}
                {selectedMentee?.first_name} {selectedMentee?.last_name} ha sido creada.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--large" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div>
            <h2>Crear Nueva Dupla de Mentoría</h2>
            <p className="modal__subtitle">Asigna un mentor a un mentee para comenzar su proceso de mentoría</p>
          </div>
          <button className="modal__close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal__body">
            {error && (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Mentor Selection */}
            <div className="form-section">
              <h3 className="form-section__title">
                <Users size={20} />
                Seleccionar Mentor
              </h3>

              {loadingMentors ? (
                <div className="loading-state">Cargando mentores...</div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Mentor *</label>
                    <select
                      className="form-select"
                      value={formData.mentor_id || ''}
                      onChange={(e) => handleMentorChange(e.target.value)}
                      required
                    >
                      <option value="">Selecciona un mentor</option>
                      {mentors.map(mentor => (
                        <option
                          key={mentor.id}
                          value={mentor.id}
                          disabled={mentor.availability === 'full'}
                        >
                          {mentor.first_name} {mentor.last_name} - {mentor.title || 'Sin título'}
                          ({mentor.current_mentees_count}/{mentor.max_mentees} mentees)
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedMentor && (
                    <div className="selection-card">
                      <div className="selection-card__header">
                        <h4>{selectedMentor.first_name} {selectedMentor.last_name}</h4>
                        {getAvailabilityBadge(selectedMentor.availability)}
                      </div>
                      <div className="selection-card__body">
                        {selectedMentor.title && <p className="selection-title">{selectedMentor.title}</p>}
                        {selectedMentor.company && <p className="selection-company">{selectedMentor.company}</p>}
                        <div className="selection-stats">
                          <div className="stat">
                            <span className="stat-label">Mentees actuales:</span>
                            <span className="stat-value">{selectedMentor.current_mentees_count}/{selectedMentor.max_mentees}</span>
                          </div>
                          {selectedMentor.rating && (
                            <div className="stat">
                              <span className="stat-label">Rating:</span>
                              <span className="stat-value">{selectedMentor.rating}/10</span>
                            </div>
                          )}
                          {selectedMentor.total_sessions && (
                            <div className="stat">
                              <span className="stat-label">Sesiones totales:</span>
                              <span className="stat-value">{selectedMentor.total_sessions}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Mentee Selection */}
            <div className="form-section">
              <h3 className="form-section__title">
                <Users size={20} />
                Seleccionar Mentee
              </h3>

              {loadingMentees ? (
                <div className="loading-state">Cargando mentees...</div>
              ) : mentees.length === 0 ? (
                <div className="empty-state-small">
                  <AlertCircle size={24} />
                  <p>No hay mentees disponibles sin mentor asignado</p>
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Mentee *</label>
                    <select
                      className="form-select"
                      value={formData.mentee_id || ''}
                      onChange={(e) => handleMenteeChange(e.target.value)}
                      required
                    >
                      <option value="">Selecciona un mentee</option>
                      {mentees.map(mentee => (
                        <option key={mentee.id} value={mentee.id}>
                          {mentee.first_name} {mentee.last_name} - {mentee.current_role || 'Sin rol'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedMentee && (
                    <div className="selection-card">
                      <div className="selection-card__header">
                        <h4>{selectedMentee.first_name} {selectedMentee.last_name}</h4>
                      </div>
                      <div className="selection-card__body">
                        {selectedMentee.current_role && <p className="selection-title">{selectedMentee.current_role}</p>}
                        {selectedMentee.company && <p className="selection-company">{selectedMentee.company}</p>}
                        {selectedMentee.career_goals && (
                          <div className="selection-goals">
                            <strong>Objetivos:</strong>
                            <p>{selectedMentee.career_goals}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Additional Info */}
            <div className="form-section">
              <h3 className="form-section__title">Información Adicional</h3>

              <div className="form-group">
                <label className="form-label">Fecha de inicio</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Objetivos de la relación (opcional)</label>
                <textarea
                  className="form-textarea"
                  rows={4}
                  value={formData.relationship_goals}
                  onChange={(e) => setFormData(prev => ({ ...prev, relationship_goals: e.target.value }))}
                  placeholder="Describe los objetivos que se esperan de esta relación de mentoría..."
                />
              </div>
            </div>
          </div>

          <div className="modal__footer">
            <button
              type="button"
              className="btn btn--outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={loading || !formData.mentor_id || !formData.mentee_id}
            >
              {loading ? 'Creando...' : 'Crear Dupla'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRelationshipModal;
